import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTriangleExclamation,
  faCircleCheck,
  faSpinner,
  faCircleMinus,
} from '@fortawesome/free-solid-svg-icons'
import type { ServiceBusHealth } from '../../../types/systemHealth'
import { healthLevelClasses, healthLevelLabel } from '../../../utils/systemHealthStatus'
import { QUEUE_DEPTH_WARNING } from '../../../constants/systemHealth'
import { cn } from '../../../shared/utils/cn'

interface ServiceBusStatusPanelProps {
  data: ServiceBusHealth | undefined
  isLoading: boolean
}

/**
 * 'neutral' is not a HealthLevel because it is not a health judgement: the
 * Service Bus path ships dormant, so an unconfigured queue is the expected
 * steady state and must not read as an outage. Colouring it red would train
 * operators to ignore the banner exactly when it starts meaning something.
 */
type ServiceBusLevel = 'neutral' | 'green' | 'yellow' | 'red'

const NEUTRAL_CLASSES = {
  dot: 'bg-gray-400',
  bg: 'bg-gray-50',
  text: 'text-gray-700',
  border: 'border-gray-200',
}

function deriveServiceBusLevel(data: ServiceBusHealth | undefined): ServiceBusLevel {
  if (!data || !data.queue.configured) return 'neutral'
  if (data.healthcheckError) return 'red'
  if ((data.queue.deadLetter ?? 0) > 0) return 'red'
  if ((data.queue.active ?? 0) >= QUEUE_DEPTH_WARNING) return 'yellow'
  return 'green'
}

function levelClasses(level: ServiceBusLevel) {
  return level === 'neutral' ? NEUTRAL_CLASSES : healthLevelClasses(level)
}

function levelLabel(level: ServiceBusLevel, data: ServiceBusHealth | undefined): string {
  if (level === 'neutral') return data ? 'Not configured' : 'Unknown'
  return healthLevelLabel(level)
}

function subLine(data: ServiceBusHealth | undefined): string {
  if (!data) return 'Awaiting status…'
  if (data.healthcheckError) return `Processor unreachable: ${data.healthcheckError}`
  if (!data.queue.configured) return 'No Service Bus connection string on the processor.'
  if (!data.queue.adminConfigured) {
    return 'Consumers are live. Queue depths need AZURE_SERVICE_BUS_ADMIN_CONNECTION_STRING (Manage SAS).'
  }
  return `Queue: ${data.queue.name}`
}

export function ServiceBusStatusPanel({ data, isLoading }: ServiceBusStatusPanelProps) {
  const level = deriveServiceBusLevel(data)
  const classes = levelClasses(level)
  const icon =
    level === 'green' ? faCircleCheck : level === 'neutral' ? faCircleMinus : faTriangleExclamation

  return (
    <div className={cn('rounded-lg border p-6', classes.bg, classes.border)}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : icon}
            className={cn('h-8 w-8', classes.text, isLoading && 'animate-spin')}
          />
          <div>
            <p className={cn('text-2xl font-semibold', classes.text)}>
              {isLoading ? 'Checking…' : levelLabel(level, data)}
            </p>
            <p className="text-sm text-gray-600">{subLine(data)}</p>
          </div>
        </div>

        <dl className="grid grid-cols-3 gap-6">
          <Metric label="Active" value={data?.queue.active} />
          <Metric
            label="Dead-letter"
            value={data?.queue.deadLetter}
            highlight={(data?.queue.deadLetter ?? 0) > 0}
          />
          <Metric label="Scheduled" value={data?.queue.scheduled} />
        </dl>
      </div>
    </div>
  )
}

function Metric({
  label,
  value,
  highlight,
}: {
  label: string
  value: number | null | undefined
  highlight?: boolean
}) {
  return (
    <div className="text-right">
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd
        className={cn(
          'text-2xl font-semibold',
          highlight ? 'text-red-700' : 'text-gray-900'
        )}
      >
        {value ?? '—'}
      </dd>
    </div>
  )
}
